import { useEffect } from "react";
import { Field, Form, Formik } from "formik";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField } from "@/components/common";
import { profileSchema } from "../utils";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth/hooks";
import { useTran } from "@/hooks";
import { Switch } from "@/components/ui/switch";
import { resolveRoleLabel } from "@/features/user-management/utils/user-role-options";

const DirtyTracker = ({ dirty, onDirtyChange }) => {
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  return null;
};

const FieldInput = ({ label, name, type = "text", required, validator }) => (
  <Field name={name} validate={validator}>
    {({ field, meta }) => (
      <FormField
        label={label}
        type={type}
        {...field}
        error={meta.touched && meta.error}
        required={required}
      />
    )}
  </Field>
);

const ProfileSection = ({
  initialValues,
  onSubmit,
  uniqueUsernameValidator,
  uniqueEmailValidator,
  onDirtyChange,
}) => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];
  const roleName =
    resolveRoleLabel(user.role, t, undefined, user?.customRoleName) ||
    t("Not available");
  return (
    <Card className="border-border/70 flex flex-col">
      <CardHeader>
        <CardTitle className="text-primary">User Information</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col flex-1">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={profileSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting, dirty }) => (
            <Form className="flex flex-col flex-1 space-y-3">
              <DirtyTracker dirty={dirty} onDirtyChange={onDirtyChange} />
              <FieldInput
                label="Username"
                name="username"
                required
                validator={uniqueUsernameValidator}
              />
              <FieldInput label="Display Name" name="name" required />
              <FieldInput
                label="Email"
                name="email"
                type="email"
                required
                validator={uniqueEmailValidator}
              />
              <Field name="company">
                {({ field }) => (
                  <FormField label="Company" disabled {...field} />
                )}
              </Field>
              <div className="flex min-h-10 flex-wrap items-center gap-2 rounded-md border px-3 py-2">
                <label className="text-sm font-medium mr-2">
                  {t("Enable Email 2FA")}:
                </label>
                <Field name="email2FAEnabled">
                  {({ field, form }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        form.setFieldValue("email2FAEnabled", checked)
                      }
                    />
                  )}
                </Field>
              </div>
              <div className="flex min-h-10 flex-wrap items-center gap-2 rounded-md border bg-muted px-3 py-2">
                <span className="text-sm font-medium mr-2">
                  {t("Role Name")}:
                </span>
                <span className="text-sm">{roleName}</span>
              </div>
              <Can
                resource="user_profile"
                action="update"
                defaultView={true}
                userPermissions={permissions}
              >
                <div className="mt-auto flex justify-end pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto"
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </Can>
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
};

export default ProfileSection;
